export const SEARCH_PAGE_SIZE = 10;

export const FACET_KEY = {
  category: "category",
  manufacturer: "manufacturer",
  price: "price",
  inStock: "in_stock"
};

export const WIRE_KEY = {
  category: "category_ids",
  manufacturer: "manufacturer_ids"
};

export const NEW_TERM_PERSIST_KEYS = [FACET_KEY.manufacturer, FACET_KEY.price, FACET_KEY.inStock];

export function stableStringify(value) {
  if (value === undefined) {
    return "null";
  }
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableStringify(item)).join(",")}]`;
  }
  const keys = Object.keys(value).sort();
  return `{${keys.map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`).join(",")}}`;
}

export function filtersEqual(left, right) {
  return stableStringify(normalizeFilters(left)) === stableStringify(normalizeFilters(right));
}

export function normalizeFilters(filters = {}) {
  const next = {};
  Object.keys(filters || {}).forEach((key) => {
    const value = filters[key];
    if (value == null || value === false) {
      return;
    }
    if (Array.isArray(value) && value.length === 0) {
      return;
    }
    next[key] = value;
  });
  return next;
}

export function getSelectedValues(filters = {}, key) {
  const value = filters[key];
  if (key === FACET_KEY.inStock) {
    return value ? [true] : [];
  }
  if (Array.isArray(value)) {
    return value;
  }
  if (value == null || value === false) {
    return [];
  }
  return [value];
}

export function isValueSelected(filters, key, value) {
  if (key === FACET_KEY.inStock) {
    return Boolean(filters?.[FACET_KEY.inStock]);
  }
  return getSelectedValues(filters, key).some((item) => String(item) === String(value));
}

export function toggleFilterValue(filters = {}, key, value, { singleSelect = false } = {}) {
  const next = { ...filters };
  if (key === FACET_KEY.inStock) {
    if (next[key]) {
      delete next[key];
    } else {
      next[key] = true;
    }
    return normalizeFilters(next);
  }
  if (singleSelect) {
    if (isValueSelected(filters, key, value)) {
      delete next[key];
    } else {
      next[key] = [value];
    }
    return normalizeFilters(next);
  }
  const selected = getSelectedValues(filters, key);
  const exists = selected.some((item) => String(item) === String(value));
  const updated = exists
    ? selected.filter((item) => String(item) !== String(value))
    : [...selected, value];
  if (updated.length === 0) {
    delete next[key];
  } else {
    next[key] = updated;
  }
  return normalizeFilters(next);
}

export function removeFilterValue(filters = {}, key, value) {
  if (key === FACET_KEY.inStock) {
    const next = { ...filters };
    delete next[key];
    return next;
  }
  return toggleFilterValue(filters, key, value, { singleSelect: key === FACET_KEY.category });
}

export function persistFiltersForNewTerm(filters = {}) {
  const next = {};
  NEW_TERM_PERSIST_KEYS.forEach((key) => {
    if (filters[key] !== undefined) {
      next[key] = filters[key];
    }
  });
  return normalizeFilters(next);
}

export function getSpecFacetKeys(config) {
  const keys = new Set();
  Object.values(config?.byCategory || {}).forEach((list) => {
    (list || []).forEach((facet) => {
      if (facet?.key) {
        keys.add(facet.key);
      }
    });
  });
  return [...keys];
}

export function isSpecFacetKey(key, config) {
  return getSpecFacetKeys(config).includes(key);
}

export function hasSpecFilters(filters = {}, config) {
  return getSpecFacetKeys(config).some((key) => getSelectedValues(filters, key).length > 0);
}

export function clearSpecFilters(filters = {}, config) {
  const specKeys = new Set(getSpecFacetKeys(config));
  const next = { ...filters };
  specKeys.forEach((key) => {
    delete next[key];
  });
  return normalizeFilters(next);
}

export function hasActiveFilters(filters = {}) {
  return Object.keys(normalizeFilters(filters)).length > 0;
}

export function findFacetConfig(config, key) {
  const universal = config?.universal || [];
  const fromUniversal = universal.find((facet) => facet.key === key);
  if (fromUniversal) {
    return fromUniversal;
  }
  const lists = Object.values(config?.byCategory || {});
  for (let i = 0; i < lists.length; i += 1) {
    const found = (lists[i] || []).find((facet) => facet.key === key);
    if (found) {
      return found;
    }
  }
  return null;
}

function bucketIdentity(min, max) {
  return `${min ?? ""}:${max ?? ""}`;
}

export function normalizeBucket(bucket) {
  if (Array.isArray(bucket)) {
    return { min: bucket[0], max: bucket[1], label: bucket[2] };
  }
  if (bucket && typeof bucket === "object") {
    return { min: bucket.min, max: bucket.max, label: bucket.label };
  }
  return null;
}

export function matchBucket(facetConfig, value) {
  const buckets = (facetConfig?.buckets || []).map(normalizeBucket).filter(Boolean);
  return (
    buckets.find((bucket) => {
      if (value && typeof value === "object" && !Array.isArray(value)) {
        return value.min === bucket.min && value.max === bucket.max;
      }
      const asString = String(value);
      return (
        (bucket.label && asString === String(bucket.label)) ||
        asString === bucketIdentity(bucket.min, bucket.max) ||
        asString === `${bucket.min}-${bucket.max}`
      );
    }) || null
  );
}

function coerceTermValue(value) {
  if (typeof value === "number") {
    return value;
  }
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "string" && value.trim() !== "" && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return value;
}

export function toWireFilters(filters = {}, config) {
  const normalized = normalizeFilters(filters);
  const wire = {};

  Object.keys(normalized).forEach((key) => {
    const facetConfig = findFacetConfig(config, key);
    const type = facetConfig?.type;

    if (key === "distributors") {
      return;
    }

    if (key === FACET_KEY.inStock) {
      wire.in_stock = true;
      return;
    }

    if (key === FACET_KEY.category) {
      wire.category_ids = getSelectedValues(normalized, key);
      return;
    }

    if (key === FACET_KEY.manufacturer) {
      wire.manufacturer_ids = getSelectedValues(normalized, key);
      return;
    }

    if (type === "range_buckets" || key === FACET_KEY.price) {
      const ranges = getSelectedValues(normalized, key)
        .map((value) => matchBucket(facetConfig, value))
        .filter(Boolean)
        .map(({ min, max }) => ({ min, max }));
      if (ranges.length === 1) {
        wire[key] = ranges[0];
      } else if (ranges.length > 1) {
        wire[key] = ranges;
      }
      return;
    }

    if (type === "boolean") {
      wire[key] = true;
      return;
    }

    wire[key] = getSelectedValues(normalized, key).map(coerceTermValue);
  });

  return wire;
}

export function serializeFiltersForRequest(filters = {}, config) {
  const wire = toWireFilters(filters, config);
  if (Object.keys(wire).length === 0) {
    return undefined;
  }
  return JSON.stringify(wire);
}

export function buildSearchRequestParams({
  searchValue,
  lookupPlace,
  filters,
  config,
  skip = 0,
  limit = SEARCH_PAGE_SIZE,
  mode = "replace"
}) {
  const inStock = lookupPlace === "internal" ? false : Boolean(filters?.[FACET_KEY.inStock]);
  const offset = mode === "replace" ? 0 : skip;
  const params = {
    offset,
    limit,
    search: searchValue,
    schema: lookupPlace,
    in_stock: inStock
  };

  if (lookupPlace === "external") {
    const serialized = serializeFiltersForRequest(filters, config);
    if (serialized) {
      params.filters = serialized;
    }
    if (mode !== "append") {
      params.include_facets = true;
    }
  }

  return params;
}
